import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { findIndex } from 'lodash';
import { addressBookApis } from '../apis';
import { sortAddressBooks } from '../helpers/addressBook';
import { PAGINATIONS } from '../constants';

const initialState = {
  billingAddress: null,
  defaultAddress: null,
  data: [],
  // TODO: This should be moved to UI reducer, temporary solution
  searchData: [],
};

const defaultFetchAddressBooksParams = {
  searchPage: 1,
  searchPageSize: PAGINATIONS.DEFAULT_SEARCH_OVERVIEW_SIZE,
};

const fetchAddressBooks = createAsyncThunk(
  'addressBook/filter',
  async ({ searchPage, searchPageSize } = defaultFetchAddressBooksParams) => {
    const response = await addressBookApis.getAddressBooks({
      searchPage,
      searchPageSize,
    });
    return response?.data;
  }
);

const searchAddressBooks = createAsyncThunk(
  'addressBook/search',
  async ({
    searchPage = defaultFetchAddressBooksParams.searchPage,
    searchPageSize = defaultFetchAddressBooksParams.searchPageSize,
    searchString,
  }) => {
    const response = await addressBookApis.getAddressBooksBySearch({
      searchPage,
      searchPageSize,
      searchString,
    });
    return response?.data;
  }
);

const getAddressBookById = createAsyncThunk(
  'addressBook/get',
  async (addressBookId, { rejectWithValue }) => {
    try {
      const { data } = await addressBookApis.getAddressBookById(addressBookId);
      return data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response?.data);
      } else {
        throw error;
      }
    }
  }
);

const fetchDefaultAddressBook = createAsyncThunk(
  'addressBook/fetchDefault',
  async () => {
    const { data } = await addressBookApis.getDefaultAddress();
    return data;
  }
);

const updateAddressBook = createAsyncThunk(
  'addressBook/update',
  async ({ addressBookId, addressBookData }, { rejectWithValue }) => {
    try {
      const { data } = await addressBookApis.updateAddressBook({
        addressBookId,
        addressBookData,
      });
      return data;
    } catch (error) {
      if (error.response) {
        throw rejectWithValue(error.response?.data);
      } else {
        throw error;
      }
    }
  }
);

const deleteAddressBook = createAsyncThunk(
  'addressBook/delete',
  async (addressBookId, { rejectWithValue }) => {
    try {
      const { data } = await addressBookApis.deleteAddressBook(addressBookId);
      return data?.addressBookId;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response?.data);
      } else {
        throw error;
      }
    }
  }
);

const createAddressBook = createAsyncThunk(
  'addressBook/create',
  async (addressBookData, { rejectWithValue }) => {
    try {
      const { data } = await addressBookApis.createAddressBook(addressBookData);
      return data;
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response?.data);
      } else {
        throw error;
      }
    }
  }
);

const slice = createSlice({
  name: 'addressBook',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAddressBooks.fulfilled, (state, { payload }) => {
        const addressBooks = payload?.addressBooks || [];
        state.data = [...addressBooks];
        state.searchData = [...addressBooks];
        addressBooks.forEach(ab => {
          if (ab.isDefault) {
            state.defaultAddress = ab;
          }
          if (ab.isBilling) {
            state.billingAddress = ab;
          }
        });
        return state;
      })
      .addCase(searchAddressBooks.fulfilled, (state, { payload }) => {
        const addressBooks = payload?.addressBooks || [];
        state.searchData = [...addressBooks];
        addressBooks.forEach(ab => {
          if (ab.isDefault) {
            state.defaultAddress = ab;
          }
          if (ab.isBilling) {
            state.billingAddress = ab;
          }
        });
        return state;
      })
      .addCase(createAddressBook.fulfilled, (state, { payload }) => {
        if (payload.isDefault) {
          state.defaultAddress = payload;
        }
        if (payload.isBilling) {
          state.billingAddress = payload;
        }
      })
      .addCase(updateAddressBook.fulfilled, (state, { payload }) => {
        const index = findIndex(
          state.data,
          a => a.addressBookId === payload.addressBookId
        );
        if (index !== -1) {
          if (payload.isDefault) {
            state.defaultAddress = payload;
            // reset default value
            state.data.forEach(item => {
              item.isDefault = false;
            });
          }
          if (payload.isBilling) {
            state.billingAddress = payload;
            state.data.forEach(item => {
              item.isBilling = false;
            });
          }
          state.data[index] = payload; // Update the item in the array
        }
      })
      .addCase(deleteAddressBook.fulfilled, (state, { payload }) => {
        if (state.defaultAddress?.addressBookId === payload) {
          state.defaultAddress = null;
        }
        if (state.billingAddress?.addressBookId === payload) {
          state.billingAddress = null;
        }
      })
      .addCase(fetchDefaultAddressBook.fulfilled, (state, { payload }) => {
        state.defaultAddress = payload;
      });
  },
});

const getAddressBooksState = state => state.addressBook;

const getAddressBooks = createSelector([getAddressBooksState], state =>
  sortAddressBooks([...state.data])
);

const getAddressBooksByPage = createSelector([getAddressBooks], data => data);

const getAddressBookLength = createSelector(
  [getAddressBooksState],
  state => state.data.length
);

const getSearchResults = createSelector([getAddressBooksState], state =>
  sortAddressBooks([...state.searchData])
);

const getDefaultAddressBook = state => state.addressBook.defaultAddress;
const getBillingAddressBook = state => state.addressBook.billingAddress;

export default {
  reducer: slice.reducer,
  actions: {
    fetchDefaultAddressBook,
    fetchAddressBooks,
    searchAddressBooks,
    getAddressBookById,
    updateAddressBook,
    deleteAddressBook,
    createAddressBook,
  },
  selectors: {
    getAddressBookLength,
    getDefaultAddressBook,
    getBillingAddressBook,
    getAddressBooksByPage,
    getSearchResults,
  },
};
